/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  applyUserPreferencesPatch,
  type PreferencePath,
  type PreferenceValue,
  resolveUserPreferences,
  type StoredUserPreferences,
  type UserPreferencesPatch,
} from '@poweralpha/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDesktopSessionQuery } from '../api';
import type { UserPreferencesContextValue } from './UserPreferencesContext';
import {
  readCachedPreferences,
  readLegacyPreferencesPatch,
  removeLegacyPreferences,
  saveUserPreferences,
  writeCachedPreferences,
} from './userPreferencesStorage';

const mergePatches = (patches: UserPreferencesPatch[]): UserPreferencesPatch => {
  const set: Record<string, unknown> = {};
  const unset = new Set<string>();
  for (const patch of patches) {
    for (const [path, value] of Object.entries(patch.set ?? {})) {
      set[path] = value;
      unset.delete(path);
    }
    for (const path of patch.unset ?? []) {
      delete set[path];
      unset.add(path);
    }
  }
  return { set, unset: [...unset] } as UserPreferencesPatch;
};

/**
 * Owns the user's preference overrides. Changes apply immediately and are saved in order. Changes
 * made before the desktop session loads are replayed on top of the server's value once it does.
 * Without a session (no desktop tables locally) settings only persist in the local cache.
 */
export const useUserPreferencesState = (): UserPreferencesContextValue => {
  const { data: session } = useDesktopSessionQuery();
  const [stored, setStored] = useState<StoredUserPreferences>(readCachedPreferences);
  const hydrated = useRef(false);
  const pendingPatches = useRef<UserPreferencesPatch[]>([]);
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  // Changes whose save failed; they ride along with the next save until one succeeds.
  const failedPatches = useRef<UserPreferencesPatch[]>([]);

  const enqueueSave = useCallback((patch: UserPreferencesPatch, onSaved?: () => void) => {
    saveQueue.current = saveQueue.current.then(async () => {
      const merged = mergePatches([...failedPatches.current, patch]);
      failedPatches.current = [];
      try {
        await saveUserPreferences(merged);
        onSaved?.();
      } catch {
        failedPatches.current = [merged];
      }
    });
  }, []);

  const applyPatch = useCallback(
    (patch: UserPreferencesPatch) => {
      setStored((current) => {
        const next = applyUserPreferencesPatch(current, patch);
        writeCachedPreferences(next);
        return next;
      });
      if (hydrated.current) {
        enqueueSave(patch);
      } else {
        pendingPatches.current.push(patch);
      }
    },
    [enqueueSave],
  );

  useEffect(() => {
    if (!session || hydrated.current) {
      return;
    }
    hydrated.current = true;
    const serverPreferences = (session.user.preferences ?? {}) as StoredUserPreferences;
    const legacyPatch = readLegacyPreferencesPatch(serverPreferences);
    const patch = mergePatches([...(legacyPatch ? [legacyPatch] : []), ...pendingPatches.current]);
    pendingPatches.current = [];
    const next = applyUserPreferencesPatch(serverPreferences, patch);
    setStored(next);
    writeCachedPreferences(next);
    if (Object.keys(patch.set ?? {}).length || patch.unset?.length) {
      // The legacy key goes only once its value is saved, so a failed migration can retry.
      enqueueSave(patch, legacyPatch ? removeLegacyPreferences : undefined);
    }
  }, [enqueueSave, session]);

  const setPreference = useCallback(
    <P extends PreferencePath>(path: P, value: PreferenceValue<P>) =>
      applyPatch({ set: { [path]: value } } as UserPreferencesPatch),
    [applyPatch],
  );
  const resetPreferences = useCallback(
    (paths: PreferencePath[]) => applyPatch({ unset: paths }),
    [applyPatch],
  );

  const preferences = useMemo(() => resolveUserPreferences(stored), [stored]);

  return useMemo(
    () => ({ preferences, stored, setPreference, resetPreferences }),
    [preferences, resetPreferences, setPreference, stored],
  );
};
